import * as React from 'react'

import CSSToolTip from '../CSSToolTip/CSSToolTip'
import LoadingEllipsis from '../LoadingEllipsis'
import { formatRequiredIndicator } from '../Required/Required'
import { RequiredInterface } from '../Required/RequiredInterface'
import './Input.scss'
import { InputInterface } from './InputInterface'

export interface InputState<T = string> {
  value?: T
  stringValue?: string
  patternValue?: string
  hasBlurred?: boolean
  keyCode?: number
  location?: any
  height?: string
}

export interface InputPropsInterface extends InputInterface, RequiredInterface {
  showCounter?: boolean
  maxLength?: number
  type?: 'text' | 'number' | 'password'
  tail?: JSX.Element
  helpTooltipText?: string | JSX.Element
  helpTooltipHeader?: string | JSX.Element
  helpTooltipPositionAbsolute?: boolean
  helpTooltipPosition?: 'top' | 'left' | 'right' | 'bottom'
  startAdornment?: JSX.Element | string
  showLoadingAnimation?: boolean
  border?: boolean
  style?: any
  key?: any
}

class Input extends React.Component<InputPropsInterface, InputState> {
  constructor(props: InputPropsInterface) {
    super(props)
    this.state = {
      value: props.value || '',
      stringValue: props.value || '',
      hasBlurred: true,
      keyCode: null,
      location: '',
    }
  }

  private input: HTMLInputElement

  public UNSAFE_componentWillReceiveProps(props: InputPropsInterface) {
    const stringValue = this.handleFormatting(props.value)
    if (this.state.hasBlurred && (this.state.value !== stringValue || this.state.stringValue !== stringValue)) {
      this.setState({
        value: stringValue,
        stringValue,
        hasBlurred: true,
      })
    }
  }

  public static defaultProps: InputPropsInterface = {
    value: '',
    className: ['Text'],
    automation: '',
    editable: true,
    borderless: false,
    autoFocus: false,
    type: 'text',
    disabled: false,
  }

  private handleOnBlur(e: Event) {
    this.setState({
      ...this.state,
      hasBlurred: true,
    })

    if (this.props.onBlur) {
      this.props.onBlur(this.state.value, this.state.stringValue)
      ;(e.target as HTMLInputElement).value = this.state.value
    }
  }

  private handleFormatting(value: string): string {
    if (this.props.formatter) {
      return this.props.formatter.render(this.props.formatter.read(value))
    }
    return value
  }

  private handlePattern(value: string): string {
    if (!this.props.pattern) {
      return value
    }
    if (this.props.pattern.exec(value)) {
      return value
    } else {
      return this.state.patternValue
    }
  }

  private handleOnChange(e: Event) {
    const { keyCode, location } = this.state
    let targetValue = (e.target as HTMLInputElement).value
    if (this.props.editable === false) {
      return
    }
    // this is for replacing key 110 on numpad keyboard to local decimalSeparator
    if (this.props.className[0] === 'Number' && keyCode === 110 && location === 3) {
      const decimalSeparator = this.props.formatter.format.numbers.decimalSeparator as string
      targetValue = targetValue.replace('.', `${decimalSeparator}`)
    }

    const stringValue: string = this.handlePattern(targetValue)
    const value = this.handleFormatting(stringValue)
    this.setState({
      value,
      stringValue,
      patternValue: stringValue,
      hasBlurred: false,
    })
    if (this.props.onChange) {
      this.props.onChange(value, stringValue)
    }
  }

  private handleOnKeyDown(e) {
    const keyCode = e.keyCode || e.which
    const location = e.location
    if (keyCode === 110 && location === 3) {
      this.setState({ keyCode, location: 3 })
    }
  }

  private handleOnKeyUp(e) {
    const keyCode = e.keyCode || e.which
    const location = e.location
    if (keyCode === 110 && location === 3) {
      this.setState({ keyCode: null, location: '' })
    }
  }

  private setFocusOnInput(): void {
    const length = this.props.value.length
    this.input.focus()
    this.input.setSelectionRange(length, length)
  }

  public componentDidMount() {
    if (this.props.autoFocus && this.input) {
      this.setFocusOnInput()
    }
  }

  public render(): JSX.Element {
    const classes = ['Controlled', 'Input']
    if (this.props.borderless) {
      classes.push('Borderless')
    }

    let label = null
    if (this.props.label) {
      const showcounter = this.props.maxLength && this.props.showCounter
      const counterSpan = showcounter ? (
        <span className="counter">{` (${this.state.value.length}/${this.props.maxLength})`}</span>
      ) : null
      const required = formatRequiredIndicator(this.props)
      label = (
        <span className="Label" data-automation={this.props.automation + '-label'}>
          {this.props.label}
          {required}
          {counterSpan}
        </span>
      )
    }

    const baseClasses = ['Controlled', 'Input']
    if (this.props.labelPosition && this.props.labelPosition === 'top') {
      baseClasses.push('LabelOnTop')
    }
    if (this.props.align && this.props.align === 'left') {
      baseClasses.push('JustifyLeft')
    }
    if (this.props.invalid) {
      baseClasses.push('invalid')
    }
    if (this.props.startAdornment) {
      baseClasses.push('withStartAdornment')
    }
    if (this.props.border) {
      baseClasses.push('withBorder')
    }

    let value = ''
    if (this.props.onChange) {
      value = this.props.value
    } else {
      value = this.state.stringValue
    }

    return (
      <div className={baseClasses.join(' ')}>
        {this.props.helpTooltipText ? (
          <div className={`Label HelperWrap${this.props.helpTooltipPositionAbsolute ? ' absolute' : ''}`}>
            {label}
            <CSSToolTip
              header={this.props.helpTooltipHeader}
              body={this.props.helpTooltipText}
              position={this.props.helpTooltipPosition}
              helpTooltipPositionAbsolute={this.props.helpTooltipPositionAbsolute}
            />
          </div>
        ) : (
          label
        )}
        {this.props.startAdornment && <div className="startAdornment">{this.props.startAdornment}</div>}
        {this.props.showLoadingAnimation ? (
          <LoadingEllipsis customised={this.props.showLoadingAnimation} />
        ) : (
          <>
            <input
              ref={(input) => {
                this.input = input
              }}
              style={this.props.style}
              className={classes.join(' ') + ' ' + this.props.className.join(' ')}
              data-automation={this.props.automation}
              data-value={this.props.dataValue || this.props.value}
              type={this.props.type}
              value={value}
              disabled={this.props.disabled}
              onChange={this.handleOnChange.bind(this)}
              onKeyDown={this.handleOnKeyDown.bind(this)}
              onKeyUp={this.handleOnKeyUp.bind(this)}
              onBlur={this.handleOnBlur.bind(this)}
              onClick={this.props.onClick}
              size={this.props.size}
              maxLength={this.props.maxLength}
              placeholder={this.props.placeHolder}
              aria-label={this.props.automation + '-label'}
            />
            {this.props.tail ? this.props.tail : null}
          </>
        )}
      </div>
    )
  }
}

export default Input
